import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function RiskReturn({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Beginner
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              10 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            ⚖️ Understanding Risk vs Return
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Learn the fundamental relationship between risk and returns in investing
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Golden Rule of Investing</h2>
              <p className="mb-4">
                There's a universal truth in investing: <strong>Higher returns come with higher risk.</strong> You cannot escape this relationship. 
                Anyone promising high returns with zero risk is either lying or doesn't understand investing.
              </p>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500 my-4">
                <p className="text-sm">
                  <strong>💡 Key Principle:</strong> Risk and return are two sides of the same coin. Your job as an investor is to find 
                  the right balance based on your goals, age, and risk appetite.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Risk-Return Spectrum in India</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Investment Type</th>
                      <th className="p-3 text-center">Risk Level</th>
                      <th className="p-3 text-right">Expected Return</th>
                      <th className="p-3 text-left">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                      <td className="p-3">Government Bonds</td>
                      <td className="p-3 text-center">Very Low 🟢</td>
                      <td className="p-3 text-right font-bold">6-7%</td>
                      <td className="p-3">RBI Bonds, PPF</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Fixed Deposits</td>
                      <td className="p-3 text-center">Low 🟢</td>
                      <td className="p-3 text-right font-bold">6-7.5%</td>
                      <td className="p-3">Bank FDs</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Corporate Bonds</td>
                      <td className="p-3 text-center">Low-Medium 🟡</td>
                      <td className="p-3 text-right font-bold">8-10%</td>
                      <td className="p-3">HDFC, Bajaj Finance</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Large-cap Stocks</td>
                      <td className="p-3 text-center">Medium 🟡</td>
                      <td className="p-3 text-right font-bold">10-15%</td>
                      <td className="p-3">TCS, Reliance, HDFC</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Mid-cap Stocks</td>
                      <td className="p-3 text-center">High 🟠</td>
                      <td className="p-3 text-right font-bold">12-18%</td>
                      <td className="p-3">Growing companies</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-red-50 dark:bg-red-900/10">
                      <td className="p-3">Small-cap Stocks</td>
                      <td className="p-3 text-center">Very High 🔴</td>
                      <td className="p-3 text-right font-bold">15-25%</td>
                      <td className="p-3">Emerging companies</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm italic">
                Notice: As risk increases (top to bottom), potential returns also increase. But higher risk also means higher chance of losses!
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Real Example: Market Crash of 2020</h2>
              <p className="mb-4">
                When COVID-19 hit in March 2020, the Nifty 50 fell by 38% in just one month (from 12,000 to 7,500). Different investment types behaved differently:
              </p>
              
              <div className="space-y-3">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">✅ Fixed Deposits & Bonds</p>
                  <p className="text-sm">Lost nothing. Continued earning steady 6-7% interest.</p>
                </div>
                
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">⚠️ Large-cap Stocks (TCS, Reliance)</p>
                  <p className="text-sm">Fell 30-40%, but recovered within 6 months. Those who held made good profits later.</p>
                </div>
                
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">❌ Small-cap Stocks</p>
                  <p className="text-sm">Fell 50-70%. Some still haven't recovered fully. High risk = high pain during crashes.</p>
                </div>
              </div>

              <p className="mt-4 text-sm">
                <strong>The lesson?</strong> In exchange for potentially higher returns, stocks expose you to significant short-term losses. 
                Can you stomach a 40% drop without panicking and selling?
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">3 Types of Risk Every Indian Investor Should Know</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">1. Market Risk (Systematic Risk)</h3>
                  <p className="text-sm mb-2">
                    When the entire market falls (like 2008 or 2020), almost all stocks fall together. You can't avoid this by diversification.
                  </p>
                  <p className="text-sm italic">Example: If Sensex crashes 20%, even great companies like TCS will likely fall 15-20%.</p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">2. Company Risk (Unsystematic Risk)</h3>
                  <p className="text-sm mb-2">
                    Risk specific to one company (bad management, fraud, competition). You CAN reduce this through diversification.
                  </p>
                  <p className="text-sm italic">Example: YES Bank collapse (2020) - only YES Bank investors lost money, not the whole market.</p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">3. Inflation Risk</h3>
                  <p className="text-sm mb-2">
                    The risk that your returns don't beat inflation, eroding your purchasing power over time.
                  </p>
                  <p className="text-sm italic">Example: Earning 6% in FD when inflation is 6% = zero real return!</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">How to Choose Your Risk Level</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Your Profile</th>
                      <th className="p-3 text-left">Recommended Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3"><strong>Young (20s-30s)</strong><br/>Long investment horizon</td>
                      <td className="p-3">70-80% Equity, 20-30% Debt<br/><span className="text-xs italic">You can take more risk</span></td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3"><strong>Mid-career (40s-50s)</strong><br/>Balancing growth & safety</td>
                      <td className="p-3">50-60% Equity, 40-50% Debt<br/><span className="text-xs italic">Balanced approach</span></td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3"><strong>Near Retirement (55+)</strong><br/>Preserving capital</td>
                      <td className="p-3">30-40% Equity, 60-70% Debt<br/><span className="text-xs italic">Safety first</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaways</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>No free lunch:</strong> Higher returns always come with higher risk.</li>
                <li><strong>Match risk to goals:</strong> Long-term goals → can take more risk. Short-term → play safe.</li>
                <li><strong>Diversify:</strong> Don't put all eggs in one basket. Spread across asset classes.</li>
                <li><strong>Sleep test:</strong> If your investments keep you awake at night, you've taken too much risk!</li>
              </ul>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/why-invest')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Why Invest?
          </Button>
          <Button 
            onClick={() => navigate('/learn/compounding')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: The Magic of Compounding
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
